<template>
  <div class="load-itinerary-steps">
    <a-steps 
      :current="currentStep" 
      size="small"
      direction="horizontal"
      labelPlacement="vertical"
      :progressDot="true"
    >
      <a-step 
        v-for="(item, index) in stepItems" 
        :key="index"
        :status="item.status"
      >
        <template slot="title">
          <a-tooltip :title="item.address || item.title" placement="top">
            <span>{{ item.title }}</span>
          </a-tooltip>
        </template>
        <template slot="progressDot" slot-scope="{ status, prefixCls }">
          <a-popover>
            <template slot="content">
              <div class="stop-details">
                <div class="stop-title">{{ item.title }}</div>
                <div v-if="item.eta" class="stop-eta">
                  <strong>ETA:</strong> {{ item.eta }}
                </div>
                <div v-if="item.address" class="stop-address">
                  <strong>Address:</strong> {{ item.address }}
                </div>
                <div v-if="item.pickups || item.dropoffs" class="stop-counts">
                  <div v-if="item.pickups">
                    <strong>Pickups:</strong> {{ item.pickups }}
                  </div>
                  <div v-if="item.dropoffs">
                    <strong>Dropoffs:</strong> {{ item.dropoffs }}
                  </div>
                </div>
              </div>
            </template>
            <span :class="`${prefixCls}-icon-dot`" />
          </a-popover>
        </template>
      </a-step>
    </a-steps>
  </div>
</template>

<script>
export default {
  name: 'LoadItinerarySteps',
  props: {
    itineraryStops: {
      type: Array,
      default: () => []
    },
    currentStatus: {
      type: String,
      default: 'active'
    }
  },
  data() {
    return {};
  },
  computed: {
    stepItems() {
      if (!this.itineraryStops || this.itineraryStops.length === 0) {
        return [
          {
            title: 'Unknown',
            status: 'wait'
          },
          {
            title: 'Unknown',
            status: 'wait'
          }
        ];
      }

      // Sort stops by sequence number to ensure proper order
      const sortedStops = [...this.itineraryStops].sort((a, b) => {
        return parseInt(a.stop_sequence_number) - parseInt(b.stop_sequence_number);
      });

      return sortedStops.map((stop, index) => {
        const isFirst = index === 0;
        const isLast = index === sortedStops.length - 1;
        
        // Use the actual stop address as the title
        let title = stop.stop_address || `Stop ${stop.stop_sequence_number}`;
        let description = stop.shipping_location_code || `Stop ${stop.stop_sequence_number}`;
        
        // Truncate long addresses for better display
        if (title.length > 30) {
          title = title.substring(0, 30) + '...';
        }
        
        return {
          title,
          description,
          eta: stop.estimated_time_of_arrival,
          address: stop.stop_address,
          pickups: stop.count_of_shipments_picked_at_stop,
          dropoffs: stop.count_of_shipments_dropped_at_stop,
          status: this.getStopStatus(index, sortedStops.length)
        };
      });
    },
    currentStep() {
      // Determine current step based on load status
      if (this.currentStatus === 'completed' || this.currentStatus === 'auction_won') {
        return this.stepItems.length - 1; // All steps completed
      } else if (this.currentStatus === 'active') {
        return 0; // At origin
      } else {
        return 0; // Default to first step
      }
    }
  },
  methods: {
    getStopStatus(index, totalStops) {
      if (this.currentStatus === 'completed' || this.currentStatus === 'auction_won') {
        return 'finish';
      } else if (this.currentStatus === 'active') {
        if (index === 0) {
          return 'process'; // Currently at origin
        } else {
          return 'wait'; // Future stops
        }
      } else {
        return 'wait';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.load-itinerary-steps {
  padding: 4px 0;
  
  :deep(.ant-steps) {
    font-size: 11px;
    
    .ant-steps-item {
      margin-right: 6px !important;
      
      &.ant-steps-small .ant-steps-item-title,
      .ant-steps-item-title {
        font-size: 10px !important;
        font-weight: 500;
        line-height: 1.2 !important;
        margin-top: 1px !important;
        max-width: 70px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-right: 12px !important;
      }
      
      .ant-steps-item-description {
        font-size: 5px !important;
        color: #666;
        margin-top: 1px !important;
        max-width: 70px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .ant-steps-item-icon {
        width: 14px !important;
        height: 14px !important;
        margin-right: 3px !important;
        
        .ant-steps-icon {
          font-size: 8px !important;
        }
      }
      
      .ant-steps-item-tail {
        margin-left: 6px !important;
        padding: 0 3px !important;
      }
    }
    
    // More specific override for ant-steps-small
    &.ant-steps-small .ant-steps-item .ant-steps-item-title {
      font-size: 10px !important;
      padding-right: 12px !important;
    }
  }
}

.stop-details {
  min-width: 200px;
  
  .stop-title {
    font-weight: 600;
    margin-bottom: 4px;
    color: #1890ff;
  }
  
  .stop-description {
    margin-bottom: 8px;
    color: #666;
  }
  
  .stop-eta,
  .stop-address,
  .stop-counts {
    margin-bottom: 4px;
    font-size: 12px;
    
    strong {
      color: #333;
    }
  }
  
  .stop-counts {
    display: flex;
    gap: 12px;
  }
}

// Responsive design
@media (max-width: 768px) {
  .load-itinerary-steps {
    .ant-steps {
      .ant-steps-item {
        .ant-steps-item-title {
          font-size: 11px;
        }
        
        .ant-steps-item-description {
          font-size: 10px;
        }
      }
    }
  }
}
</style>
